import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { CardConfidence } from 'containers/Intelligence/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';
import { formatNumber } from 'utils/formatters';

/**
 * The catalogue's four bands. `insufficient` is both a real score below 0.40 and the branch the FR
 * requires when a card cannot be scored at all: an unscored card says so, it never degrades into a
 * `low` band or a `0%` reading.
 */
export type ConfidenceBadgeBand = CardConfidence['band'];

export interface IConfidenceBadgeProps {
  band: ConfidenceBadgeBand;
  /** Omitted or `null` when no score exists. `0` is a real, publishable score and still renders. */
  score?: number | null;
  className?: string;
}

interface IBandTreatment {
  tint: string;
  tintOpacity: number;
  border: string;
  ink: string;
}

/**
 * The screens, not the component library, are authoritative here (AF-3 ruling, plan.md): the library
 * publishes this as a pill tinted from one hue, but every instance drawn on a card is a 36 x r10 box
 * whose fill, border and ink are three independent values. Measured off the Customer Base export
 * (design/section-customer-base-svg-diff-2026-08-11.md row 11).
 *
 * Two of the three measured hues that had no token now land here as literals rather than as
 * stand-ins: medium's border #A77A2C and high's border #91D5AD, the latter Figma's own
 * `color/Turquoise/Vista Blue 20`. Both read identically on Layer 1's own nodes
 * (PLA-8815 design/card-customer-engagement-devmode-2026-08-12.md F3) and on Customer 360's
 * `3583-8153` (PLA-8828 design/figma-spec.md § 4.2), so correcting them converges both screens on
 * the value each measured. They stay literals because tokens.ts is held by the concurrent layout
 * pass. High's ink #16834F now joins them — see the note on `HIGH_INK` below.
 */
const HIGH_BORDER = tokens.color['badge-positive-border'];
const MEDIUM_BORDER = tokens.color['badge-warning-border'];
/* The ink Figma draws on the high band (PLA-8815 card-customer-lifecycle-holdings-devmode § badge,
   component-inventory § confidence badge). ACCESSIBILITY TRADE-OFF: it reads 2.69:1 against the
   badge's own #15382D fill, below the 4.5:1 SC 1.4.3 floor for this 12px text, where the
   --is-positive token it replaces reads 5.08:1. Applied on the ruling that the drawn value
   wins. PLA-8828. */
const HIGH_INK = tokens.color['badge-positive-ink'];

const BAND_TREATMENTS: Record<ConfidenceBadgeBand, IBandTreatment> = {
  high: {
    tint: tokens.color['tint-positive-bg'],
    tintOpacity: 1,
    border: HIGH_BORDER,
    ink: HIGH_INK
  },
  medium: {
    tint: tokens.color.warning,
    tintOpacity: 0.29,
    border: MEDIUM_BORDER,
    ink: tokens.color.warning
  },
  low: {
    tint: tokens.color.negative,
    tintOpacity: 0.29,
    border: tokens.color.negative,
    ink: tokens.color.negative
  },
  // The mockups never draw an unscored card, so this band keeps the section's neutral chip treatment
  // rather than inventing a fourth confidence hue.
  insufficient: {
    tint: tokens.color['surface-card-alt'],
    tintOpacity: 1,
    border: tokens.color['border-subtle'],
    ink: tokens.color['text-muted']
  }
};

const BAND_LABEL_KEYS: Record<ConfidenceBadgeBand, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  insufficient: 'Insufficient'
};

// `tokens.radius` steps 4/8/12/18/pill and `tokens.space` stops at 32, so neither the measured 10px
// corner nor the 36px box has a token to name it. Nor does the 13px ink: the type scale jumps 12.5
// (body) to 15 (card-title). Solved from the two badge strings in `Group 40450.svg` by dividing the
// outlined ink width by the string's advance width — 12.94px and 12.97px, independently.
const BADGE_HEIGHT = '36px';
const BADGE_RADIUS = '10px';

const BadgeWrapper = styled.span<{ $band: ConfidenceBadgeBand; $perx3: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: ${BADGE_HEIGHT};
  padding: 0 ${tokens.space['12']};
  /* 0.8px, not 1: the chip carries the file's recurring hairline stroke on both screens
     (PLA-8828 figma-spec.md § 4.2, PLA-8815 card-customer-engagement-devmode § Border). */
  /* Round 4 (user): the redesign drops the badge outline and keeps the tint and the ink. The
     stroke stays in the box at transparent rather than going to border:none, so the badge
     holds the same 36px height and the same text offset in both readings. */
  border: ${tokens.stroke.hairline} solid
    ${({ $band, $perx3 }) => ($perx3 ? 'transparent' : BAND_TREATMENTS[$band].border)};
  border-radius: ${BADGE_RADIUS};
  color: ${({ $band }) => BAND_TREATMENTS[$band].ink};
  font-size: ${tokens.type['confidence-badge-size']};
  font-weight: ${tokens.type['confidence-badge-weight']};
  line-height: ${tokens.type['confidence-badge-line']};
  white-space: nowrap;

  /* A token is a CSS custom-property reference, so an alpha cannot be folded into its value and
     color-mix() is outside this repo's browser floor. The two bands the screens tint at 29% paint
     the solid token on this layer and carry the alpha here instead. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: inherit;
    background: ${({ $band }) => BAND_TREATMENTS[$band].tint};
    opacity: ${({ $band }) => BAND_TREATMENTS[$band].tintOpacity};
  }
`;

const BadgeText = styled.span`
  position: relative;
`;

const ConfidenceBadge: React.FC<IConfidenceBadgeProps> = ({ band, score, className }) => {
  const { t } = useMultiLanguageTranslation();
  const isPerx3 = usePerx3Redesign();

  const bandLabel = t(`${TCustomerInsights}.Confidence.${BAND_LABEL_KEYS[band]}`);
  const hasScore = band !== 'insufficient' && typeof score === 'number' && isFinite(score);
  const label = hasScore
    ? t(`${TCustomerInsights}.Confidence.WithScore`, {
        band: bandLabel,
        score: formatNumber(score)
      })
    : bandLabel;

  return (
    <BadgeWrapper
      className={className}
      $band={band}
      $perx3={isPerx3}
      data-testid="confidence-badge"
      data-band={band}
    >
      <BadgeText>{label}</BadgeText>
    </BadgeWrapper>
  );
};

export default ConfidenceBadge;
